import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar';
import Header from '../Header';
import { useAuth } from '../context/AuthContext';

// Simplified type definitions
interface Administrator {
  id: number;
  name: string;
  status: string;
}

interface Underwriter {
  id: number;
  underwriter_name: string;
  status: string;
}

const ApplicationInsurers = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  
  // State for administrators
  const [administrators, setAdministrators] = useState<Administrator[]>([]);
  const [underwriters, setUnderwriters] = useState<Underwriter[]>([]);
  const [loading, setLoading] = useState({
    admins: true,
    underwriters: true
  });

  // Fetch administrators from API
  const fetchAdministrators = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const response = await fetch(`${API_URL}/api/administrators`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch administrators`);
      }

      const data = await response.json();
      
      if (data.success && Array.isArray(data.data)) {
        // Map and filter administrators
        const mappedAdmins = data.data.map((admin: any) => ({
          id: admin.id || 0,
          name: admin.name || admin.administrator_name || admin.administratorName || 'Unnamed',
          status: admin.status || 'Unknown',
        }));
        
        // Filter only active administrators
        const activeAdmins = mappedAdmins.filter((admin: Administrator) => {
          const status = String(admin.status || '').toLowerCase().trim();
          return status === 'active' || 
                 status === '1' || 
                 status === 'true' || 
                 status === 'enabled' ||
                 status === 'approved' ||
                 status === 'live';
        });
        
        setAdministrators(activeAdmins);
      }
    } catch (err: any) {
      console.error('Error fetching administrators:', err);
    } finally {
      setLoading(prev => ({ ...prev, admins: false }));
    }
  };

  // Fetch underwriters from API
  const fetchUnderwriters = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const response = await fetch(`${API_URL}/api/underwriters`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch underwriters`);
      }

      const data = await response.json();
      
      if (data.success && Array.isArray(data.data)) {
        // Filter only active underwriters
        const activeUnderwriters = data.data
          .filter((underwriter: any) => {
            const status = String(underwriter.status || '').toLowerCase().trim();
            return status === 'active' || 
                   status === '1' || 
                   status === 'true' || 
                   status === 'enabled';
          })
          .map((underwriter: any) => ({
            id: underwriter.id || 0,
            underwriter_name: underwriter.underwriter_name || 'Unknown',
            status: underwriter.status || 'Active'
          }));
        
        setUnderwriters(activeUnderwriters);
      }
    } catch (err: any) {
      console.error('Error fetching underwriters:', err);
    } finally {
      setLoading(prev => ({ ...prev, underwriters: false }));
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchAdministrators();
    fetchUnderwriters();
  }, []);

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  // Safe get first character function
  const getFirstCharacter = (name?: string) => {
    if (!name || typeof name !== 'string') return '?';
    return name.charAt(0).toUpperCase();
  };

  // Handle administrator click
  const handleAdministratorClick = (admin: Administrator) => {
    navigate('/application-categories', { 
      state: { 
        type: 'administrator',
        insurerId: admin.id,
        insurerName: admin.name,
        insurerStatus: admin.status
      } 
    });
  };

  // Handle underwriter click
  const handleUnderwriterClick = (underwriter: Underwriter) => {
    navigate('/application-categories', { 
      state: { 
        type: 'underwriter',
        insurerId: underwriter.id,
        insurerName: underwriter.underwriter_name,
        insurerStatus: underwriter.status
      } 
    });
  };

  return (
    <>
      <Sidebar 
        isVisible={isSidebarVisible} 
        toggleSidebar={toggleSidebar} 
      />

      <div className={`transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header 
          title="Application Insurers"
          user={user}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={false}
        />

        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-bold text-gray-900">Application Insurers</h1>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setLoading({ admins: true, underwriters: true });
                      fetchAdministrators();
                      fetchUnderwriters();
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center"
                    disabled={loading.admins || loading.underwriters}
                  >
                    <svg className={`w-4 h-4 mr-2 ${loading.admins || loading.underwriters ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </button>
                  <button
                    onClick={() => navigate('/process-definitions')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Back to Process Definitions
                  </button>
                </div>
              </div>
              <p className="text-gray-600">
                Click on an administrator or underwriter to manage their application categories.
              </p>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Active Administrators */}
              <div>
                <div className="bg-white rounded-lg shadow-md overflow-hidden h-full">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">Active Administrators</h2>
                        <p className="text-sm text-gray-600 mt-1">
                          Insurance administrators • Status: Active
                        </p>
                      </div>
                      <div className="text-right">
                        {loading.admins ? (
                          <div className="flex items-center">
                            <svg className="animate-spin h-4 w-4 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-blue-600 text-xs font-semibold">Loading...</span>
                          </div>
                        ) : (
                          <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                            {administrators.length} Active
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-[calc(100vh-250px)]">
                    {loading.admins ? (
                      <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <p className="text-gray-500 mt-3">Loading administrators...</p>
                      </div>
                    ) : administrators.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <div className="text-4xl mb-2">🏢</div>
                        <p className="text-lg">No active administrators found</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {administrators.map((admin) => (
                          <div 
                            key={admin.id} 
                            className="p-5 hover:bg-blue-50 transition-colors duration-150 cursor-pointer"
                            onClick={() => handleAdministratorClick(admin)}
                          >
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                  <span className="text-blue-600 font-bold text-lg">
                                    {getFirstCharacter(admin.name)}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4 flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <h3 className="text-base font-semibold text-gray-900 truncate">
                                    {admin.name}
                                    <span className="ml-2 text-blue-600 text-sm">→</span>
                                  </h3>
                                  <span className="flex items-center ml-2">
                                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5"></span>
                                    <span className="text-xs text-gray-500 font-medium">{admin.status}</span>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Active Underwriters */}
              <div>
                <div className="bg-white rounded-lg shadow-md overflow-hidden h-full">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-purple-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">Active Underwriters</h2>
                        <p className="text-sm text-gray-600 mt-1">
                          Underwriters • Status: Active
                        </p>
                      </div>
                      <div className="text-right">
                        {loading.underwriters ? (
                          <div className="flex items-center">
                            <svg className="animate-spin h-4 w-4 text-purple-600 mr-2" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-purple-600 text-xs font-semibold">Loading...</span>
                          </div>
                        ) : (
                          <span className="bg-purple-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                            {underwriters.length} Active
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-[calc(100vh-250px)]">
                    {loading.underwriters ? (
                      <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                        <p className="text-gray-500 mt-3">Loading underwriters...</p>
                      </div>
                    ) : underwriters.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <div className="text-4xl mb-2">📊</div>
                        <p className="text-lg">No active underwriters found</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {underwriters.map((underwriter) => (
                          <div 
                            key={underwriter.id} 
                            className="p-5 hover:bg-purple-50 transition-colors duration-150 cursor-pointer"
                            onClick={() => handleUnderwriterClick(underwriter)}
                          >
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                                  <span className="text-purple-600 font-bold text-lg">
                                    {getFirstCharacter(underwriter.underwriter_name)}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4 flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <h3 className="text-base font-semibold text-gray-900 truncate">
                                    {underwriter.underwriter_name}
                                    <span className="ml-2 text-purple-600 text-sm">→</span>
                                  </h3>
                                  <span className="flex items-center ml-2">
                                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5"></span>
                                    <span className="text-xs text-gray-500 font-medium">{underwriter.status}</span>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                    <span className="text-blue-600 text-xl">🏢</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Active Administrators</p>
                    <p className="text-2xl font-bold text-gray-900">{administrators.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                    <span className="text-purple-600 text-xl">📊</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Active Underwriters</p>
                    <p className="text-2xl font-bold text-gray-900">{underwriters.length}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ApplicationInsurers;